import { useCallback, useEffect, useRef, useState } from 'react';
import SessionService from '../../services/SessionService';
import { pickRecorderMime } from '../../utils/pickRecorderMime';
import {
  REALTIME_SAMPLE_RATE,
  WORKLET_SOURCE,
  parseRealtimeMessage,
  realtimeErrorMessage,
  realtimeProtocols,
  realtimeStreamUrl,
  realtimeSupported,
} from './realtimeVoice';

/**
 * Registrar — voz em tempo real. Enquanto o usuário fala, o texto reconhecido aparece (parciais trocados pelos finais);
 * ao parar, o texto final chega em menos de um segundo e segue direto para a interpretação. Parcial nunca dispara ação.
 *
 * Integridade: se a conexão cai, o que já foi reconhecido e o áudio gravado no navegador ficam guardados; a tela
 * oferece "transcrever o áudio gravado" (modo gravado, whisper-1) ou usar o texto reconhecido para revisar.
 */
export type RealtimeVoiceState = 'idle' | 'connecting' | 'listening' | 'finalizing' | 'failed';

export interface RealtimeVoiceResult {
  text: string;
  /** A gravação foi cortada (tela em segundo plano, ligação, microfone caiu): o texto vai para revisão, não age sozinho. */
  interrupted?: boolean;
  durationSeconds?: number;
  stopToFinalMs?: number | null;
}

export interface RealtimeVoiceFailure {
  code: string;
  message: string;
  /** O que já tinha sido reconhecido (pode estar incompleto). */
  partialText: string;
  /** Áudio gravado no navegador, para o modo gravado. */
  recording: Blob | null;
  recordingExt: string;
  durationMs: number;
}

interface Options {
  sectorId: number;
  requestId: () => string;
  maxMs: number;
  onDone: (result: RealtimeVoiceResult) => void;
  onFailure: (failure: RealtimeVoiceFailure) => void;
  onCancelled?: () => void;
}

const FINAL_TIMEOUT_MS = 15_000;

function createCaptureContext(): AudioContext {
  const forceNative = typeof window !== 'undefined' && (window as unknown as { __mendsRtNativeRate?: boolean }).__mendsRtNativeRate;
  if (!forceNative) {
    try {
      return new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
    } catch {
      // navegador sem suporte à taxa pedida: segue na taxa nativa e o worklet reamostra
    }
  }
  return new AudioContext();
}

export function useRealtimeVoice({ sectorId, requestId, maxMs, onDone, onFailure, onCancelled }: Options) {
  const [state, setState] = useState<RealtimeVoiceState>('idle');
  const [partial, setPartial] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const nodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const extRef = useRef('webm');
  const queueRef = useRef<ArrayBuffer[]>([]);
  const readyRef = useRef(false);
  /** Stop pedido antes de o servidor ficar pronto: sai logo depois do áudio enfileirado. */
  const stopPendingRef = useRef(false);
  /** REG-06: interrupção (tela saiu do primeiro plano / microfone caiu) finaliza o que foi captado e marca para revisão. */
  const interruptedRef = useRef(false);
  const detachRef = useRef<() => void>(() => {});
  const settledRef = useRef(false);
  const startedAtRef = useRef(0);
  const partialRef = useRef('');
  const stopTimerRef = useRef<number | null>(null);
  const finalTimerRef = useRef<number | null>(null);
  const callbacks = useRef({ onDone, onFailure, onCancelled });
  callbacks.current = { onDone, onFailure, onCancelled };

  const clearTimers = () => {
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    if (finalTimerRef.current) window.clearTimeout(finalTimerRef.current);
    stopTimerRef.current = null;
    finalTimerRef.current = null;
  };

  /** Fecha microfone e captura (o MediaRecorder entrega o último pedaço no onstop). */
  const releaseAudio = useCallback(() => {
    detachRef.current();
    detachRef.current = () => {};
    try { nodeRef.current?.port.postMessage('flush'); } catch { /* ignore */ }
    try { nodeRef.current?.disconnect(); } catch { /* ignore */ }
    nodeRef.current = null;
    if (recRef.current?.state === 'recording') {
      try { recRef.current.stop(); } catch { /* ignore */ }
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx) void ctx.close().catch(() => undefined);
  }, []);

  const closeSocket = () => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && ws.readyState <= WebSocket.OPEN) {
      try { ws.close(1000, 'done'); } catch { /* ignore */ }
    }
  };

  /** Áudio local pronto (espera o último ondataavailable do MediaRecorder). */
  const recording = (): Promise<Blob | null> => new Promise((resolve) => {
    const rec = recRef.current;
    const finish = () => resolve(chunksRef.current.length ? new Blob(chunksRef.current, { type: rec?.mimeType || 'audio/webm' }) : null);
    if (!rec || rec.state === 'inactive') {
      finish();
      return;
    }
    rec.addEventListener('stop', () => finish(), { once: true });
  });

  const fail = useCallback(async (code: string, message?: string, serverText?: string) => {
    if (settledRef.current) return;
    settledRef.current = true;
    clearTimers();
    releaseAudio();
    closeSocket();
    const blob = await recording();
    setState('failed');
    callbacks.current.onFailure({
      code,
      message: message || realtimeErrorMessage(code),
      partialText: (serverText ?? partialRef.current).trim(),
      recording: blob,
      recordingExt: extRef.current,
      durationMs: Date.now() - startedAtRef.current,
    });
  }, [releaseAudio]);

  const send = (data: ArrayBuffer) => {
    const ws = wsRef.current;
    if (readyRef.current && ws?.readyState === WebSocket.OPEN) ws.send(data);
    else queueRef.current.push(data);
  };

  /** Fim da fala: manda o resto do áudio e pede o texto final. */
  const stop = useCallback(() => {
    if (settledRef.current || (state !== 'listening' && state !== 'connecting')) return;
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    stopTimerRef.current = null;
    setState('finalizing');
    releaseAudio();
    // O flush do worklet chega pela porta logo depois; o stop vai na sequência.
    window.setTimeout(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || !readyRef.current) {
        // Ainda conectando: o stop sai assim que o servidor ficar pronto.
        stopPendingRef.current = true;
        return;
      }
      ws.send(JSON.stringify({ type: 'stop' }));
    }, 60);
    finalTimerRef.current = window.setTimeout(() => void fail('timeout'), FINAL_TIMEOUT_MS);
  }, [state, releaseAudio, fail]);

  /** Desistiu (arrastou para fora / fechou): nada é transcrito nem enviado. */
  const cancel = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    clearTimers();
    try { wsRef.current?.send(JSON.stringify({ type: 'cancel' })); } catch { /* ignore */ }
    releaseAudio();
    closeSocket();
    setState('idle');
    setPartial('');
    callbacks.current.onCancelled?.();
  }, [releaseAudio]);

  const start = useCallback(async (): Promise<boolean> => {
    if (!realtimeSupported()) return false;
    settledRef.current = false;
    readyRef.current = false;
    stopPendingRef.current = false;
    interruptedRef.current = false;
    queueRef.current = [];
    partialRef.current = '';
    chunksRef.current = [];
    setPartial('');
    setNotice(null);
    setState('connecting');
    startedAtRef.current = Date.now();

    // AudioContext criado no gesto do usuário (Safari exige); o microfone vem logo depois. Pedir 24 kHz deixa a
    // reamostragem com o filtro do navegador (a linear do worklet, sem filtro, só entra se o navegador recusar a taxa).
    const ctx = createCaptureContext();
    ctxRef.current = ctx;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch {
      ctxRef.current = null;
      void ctx.close().catch(() => undefined);
      setState('idle');
      throw new Error('mic_denied');
    }
    if (settledRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return true;
    }
    streamRef.current = stream;

    // Ligação, troca de app ou microfone desligado no meio da fala: fecha a fala com o que já foi captado.
    const onInterrupt = () => {
      if (settledRef.current || interruptedRef.current) return;
      interruptedRef.current = true;
      setNotice('Gravação interrompida — o que foi captado vai para o campo para você revisar.');
      stopRef.current();
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') onInterrupt(); };
    stream.getAudioTracks().forEach((track) => track.addEventListener('ended', onInterrupt));
    document.addEventListener('visibilitychange', onVisibility);
    detachRef.current = () => {
      stream.getAudioTracks().forEach((track) => track.removeEventListener('ended', onInterrupt));
      document.removeEventListener('visibilitychange', onVisibility);
    };

    // Cópia local (modo gravado se o tempo real cair).
    try {
      const picked = pickRecorderMime();
      extRef.current = picked.ext;
      const rec = new MediaRecorder(stream, picked.mime ? { mimeType: picked.mime } : undefined);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start(500);
      recRef.current = rec;
    } catch {
      recRef.current = null;
    }

    // Captura PCM 24 kHz no worklet.
    try {
      await ctx.resume();
      const moduleUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(moduleUrl);
      URL.revokeObjectURL(moduleUrl);
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'mends-pcm-capture');
      node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => send(e.data);
      source.connect(node);
      nodeRef.current = node;
    } catch {
      void fail('capture_failed', 'Não consegui capturar o áudio do microfone em tempo real.');
      return true;
    }

    // Sessão no servidor (JWT no subprotocolo).
    const token = SessionService.getToken() || '';
    const ws = new WebSocket(realtimeStreamUrl(sectorId, requestId()), realtimeProtocols(token));
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      const msg = parseRealtimeMessage(ev.data);
      if (!msg) return;
      switch (msg.type) {
        case 'ready': {
          readyRef.current = true;
          setState((s) => (s === 'connecting' ? 'listening' : s));
          for (const item of queueRef.current) ws.send(item);
          queueRef.current = [];
          if (stopPendingRef.current) {
            stopPendingRef.current = false;
            ws.send(JSON.stringify({ type: 'stop' }));
          }
          break;
        }
        case 'partial':
          partialRef.current = msg.text;
          setPartial(msg.text);
          break;
        case 'reconnecting':
          setNotice('Reconectando a transcrição…');
          break;
        case 'limit':
          setNotice(`Limite de ${msg.seconds}s atingido — finalizando.`);
          setState('finalizing');
          releaseAudio();
          break;
        case 'done':
          if (settledRef.current) return;
          settledRef.current = true;
          clearTimers();
          releaseAudio();
          closeSocket();
          setState('idle');
          setNotice(null);
          callbacks.current.onDone({
            text: msg.text.trim(), durationSeconds: msg.durationSeconds, stopToFinalMs: msg.stopToFinalMs,
            interrupted: interruptedRef.current,
          });
          break;
        case 'error':
          void fail(msg.code, msg.message || undefined, msg.text);
          break;
      }
    };
    ws.onerror = () => { /* o onclose decide */ };
    ws.onclose = () => {
      if (!settledRef.current) void fail(readyRef.current ? 'connection_lost' : 'gateway_unavailable');
    };

    // Trava de duração (o servidor também limita).
    stopTimerRef.current = window.setTimeout(() => {
      stopTimerRef.current = null;
      setNotice('Tempo máximo de gravação — finalizando.');
      stopRef.current();
    }, maxMs);
    return true;
  }, [sectorId, requestId, maxMs, fail, releaseAudio]);

  const stopRef = useRef(stop);
  stopRef.current = stop;

  const reset = useCallback(() => {
    setState('idle');
    setPartial('');
    setNotice(null);
  }, []);

  useEffect(() => () => {
    settledRef.current = true;
    clearTimers();
    try { wsRef.current?.send(JSON.stringify({ type: 'cancel' })); } catch { /* ignore */ }
    releaseAudio();
    closeSocket();
  }, [releaseAudio]);

  return { state, partial, notice, start, stop, cancel, reset };
}
